/**
 * useInvoiceBalance Hook
 *
 * Fetches invoice balance information from the invoice_balances view.
 * Provides calculated fields including amount_due, payment totals, and legacy status.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase-client'

/**
 * Hook to fetch invoice balance from the invoice_balances view
 * @param {string} invoiceId - The invoice ID to fetch balance for
 * @param {Object} options - Additional options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @returns {Object} { balance, isLoading, error, refetch }
 */
export function useInvoiceBalance(invoiceId, options = {}) {
  const [balance, setBalance] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const { autoFetch = true } = options

  /**
   * Fetch invoice balance from the database view
   */
  const fetchBalance = useCallback(async () => {
    if (!invoiceId) {
      setBalance(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data, error: queryError } = await supabase
        .from('invoice_balances')
        .select('*')
        .eq('invoice_id', invoiceId)
        .single()

      if (queryError) {
        if (queryError.code === 'PGRST116') {
          throw new Error('Invoice not found')
        }
        throw new Error(queryError.message)
      }

      setBalance({
        invoice_id: data.invoice_id,
        invoice_no: data.invoice_no,
        customer_id: data.customer_id,
        customer_name: data.customer_name,
        invoice_total: parseFloat(data.invoice_total) || 0,
        amount_paid: parseFloat(data.amount_paid) || 0,
        amount_credited: parseFloat(data.amount_credited) || 0,
        amount_due: parseFloat(data.amount_due) || 0,
        calculated_status: data.calculated_status,
        is_legacy_paid: data.is_legacy_paid || false,
        xero_invoice_id: data.xero_invoice_id,
        invoice_date: data.invoice_date,
        payment_count: parseInt(data.payment_count) || 0,
        credit_note_count: parseInt(data.credit_note_count) || 0
      })
    } catch (err) {
      console.error('[useInvoiceBalance] Error fetching balance:', err)
      setError(err.message)
      setBalance(null)
    } finally {
      setIsLoading(false)
    }
  }, [invoiceId])

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && invoiceId) {
      fetchBalance()
    }
  }, [autoFetch, invoiceId, fetchBalance])

  return {
    balance,
    isLoading,
    error,
    refetch: fetchBalance
  }
}

/**
 * Hook to fetch multiple invoice balances for a customer
 * @param {string} customerId - The customer ID to fetch balances for
 * @param {Object} options - Additional options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {string} options.status - Filter by calculated_status
 * @returns {Object} { balances, isLoading, error, refetch }
 */
export function useCustomerInvoiceBalances(customerId, options = {}) {
  const [balances, setBalances] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const { autoFetch = true, status } = options

  /**
   * Fetch all invoice balances for the customer
   */
  const fetchBalances = useCallback(async () => {
    if (!customerId) {
      setBalances([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('invoice_balances')
        .select('*')
        .eq('customer_id', customerId)
        .order('invoice_date', { ascending: false })

      if (status) {
        query = query.eq('calculated_status', status)
      }

      const { data, error: queryError } = await query

      if (queryError) {
        throw new Error(queryError.message)
      }

      setBalances(
        (data || []).map(item => ({
          invoice_id: item.invoice_id,
          invoice_no: item.invoice_no,
          customer_id: item.customer_id,
          customer_name: item.customer_name,
          invoice_total: parseFloat(item.invoice_total) || 0,
          amount_paid: parseFloat(item.amount_paid) || 0,
          amount_credited: parseFloat(item.amount_credited) || 0,
          amount_due: parseFloat(item.amount_due) || 0,
          calculated_status: item.calculated_status,
          is_legacy_paid: item.is_legacy_paid || false,
          xero_invoice_id: item.xero_invoice_id,
          invoice_date: item.invoice_date,
          payment_count: parseInt(item.payment_count) || 0,
          credit_note_count: parseInt(item.credit_note_count) || 0
        }))
      )
    } catch (err) {
      console.error('[useCustomerInvoiceBalances] Error fetching balances:', err)
      setError(err.message)
      setBalances([])
    } finally {
      setIsLoading(false)
    }
  }, [customerId, status])

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && customerId) {
      fetchBalances()
    }
  }, [autoFetch, customerId, fetchBalances])

  return {
    balances,
    isLoading,
    error,
    refetch: fetchBalances
  }
}

export default useInvoiceBalance
